import { Flex, Spinner } from '@chakra-ui/react';
import { isBrowser } from 'constants/browser';
import { useAppSelector } from 'modules/Editor/store';
import { selectIsAllFontsLoaded } from 'modules/Editor/store/pageSelector';
import { memo, useEffect, useMemo, useState } from 'react';
import { isTest } from 'utils/environment';
import { getWordCloudData } from 'widgets/ChartWidgets/WordCloud/WordCloud.helpers';
import { ReadOnlyRenderType } from 'widgets/Widget.types';
import { useWidget } from 'widgets/sdk';
import { useWordCloudExportStore } from 'widgets/store/WordCloudExportStore';

import { ChartWidgetData } from '../ChartWidget.types';
import WordCloud from './WordCloud';
import { COLOR_STEPS } from './WordCloudWidget.config';

type ResizeConfig = {
  resizeWidth?: number;
  resizeHeight?: number;
  isResizing?: boolean;
};

export const WordCloudWrapper = memo(({ resizeWidth, resizeHeight, isResizing }: ResizeConfig) => {
  const { seriesData, widthPx, heightPx, seriesMeta, dataLabels, generalOptions, widgetId, renderType } =
    useWidget<ChartWidgetData>();
  const exportData = useWordCloudExportStore();
  const isAllFontLoaded = useAppSelector(selectIsAllFontsLoaded);

  // export loads the font before rendering the widget so it doesn't need the font load re-render
  const isExport = renderType === ReadOnlyRenderType.ExportStructureTreeList;
  const [reRender, setReRender] = useState(isExport);

  const { colors: widgetColors, colorSteps } = seriesMeta;
  const colors = widgetColors.slice(0, colorSteps ?? COLOR_STEPS);
  const { style } = dataLabels;
  const { fontFamily, fontStyle, fontWeight } = style;
  const { padding, rotation } = generalOptions;

  const data = useMemo(() => {
    const listText = seriesData[0].data.map((text: string | number) => text);
    const listValues = seriesData[1].data.map((value: string | number) => value);

    return getWordCloudData(listText, listValues, colors);
  }, [colors, seriesData]);

  useEffect(() => {
    if (!isAllFontLoaded && !isTest) return;

    setTimeout(() => {
      setReRender(true);
    }, 150);
  }, [isAllFontLoaded]);

  if (isBrowser.SAFARI && isResizing) {
    return (
      <Flex w='full' align='center' justify='center' h='full'>
        <Spinner />
      </Flex>
    );
  }

  // have the font ready to properly calculate the content layout
  if (!isAllFontLoaded && isExport && !isTest) return null;
  const reRenderStyle = !reRender ? { width: 'unset', opacity: 0 } : undefined;

  return (
    <Flex w='full' h='full' style={reRenderStyle} aria-hidden='true'>
      <WordCloud
        data={data}
        width={isResizing ? resizeWidth : widthPx}
        height={isResizing ? resizeHeight : heightPx}
        font={fontFamily}
        fontStyle={fontStyle}
        fontWeight={fontWeight}
        rotate={rotation}
        padding={padding}
        // override the cloudwords with export data (if available - else default to the normal data)
        exportData={isExport ? exportData?.[widgetId] : undefined}
      />
    </Flex>
  );
});

WordCloudWrapper.displayName = 'WordCloudWrapper';
